export type GameMediaKind = "image" | "video" | "audio" | "unknown"

export interface GameMediaItem {
	url: string
	kind: GameMediaKind
}

const blobUrlKinds = new Map<string, GameMediaKind>()

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg", "avif", "heic", "heif"])
const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "webm", "avi", "m4v", "mkv"])
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "ogg", "m4a", "aac", "flac", "opus"])

export const normalizeMediaUrl = (url: string) => url.split("?")[0]

const getExtension = (url: string) => {
	const normalized = normalizeMediaUrl(url)
	const cleanUrl = normalized.startsWith("blob:") ? normalized : decodeURIComponent(normalized)
	const lastSegment = cleanUrl.split("/").pop() || ""
	const extension = lastSegment.split(".").pop()
	return extension?.toLowerCase() || ""
}

export const inferMediaKindFromMime = (mimeType?: string | null): GameMediaKind => {
	if (!mimeType) return "unknown"
	if (mimeType.startsWith("image/")) return "image"
	if (mimeType.startsWith("video/")) return "video"
	if (mimeType.startsWith("audio/")) return "audio"
	return "unknown"
}

export const inferMediaKind = (url?: string | null, mimeType?: string | null): GameMediaKind => {
	const mimeKind = inferMediaKindFromMime(mimeType)
	if (mimeKind !== "unknown") return mimeKind
	if (!url) return "unknown"

	const registeredKind = blobUrlKinds.get(url)
	if (registeredKind) return registeredKind

	const extension = getExtension(url)
	if (IMAGE_EXTENSIONS.has(extension)) return "image"
	if (VIDEO_EXTENSIONS.has(extension)) return "video"
	if (AUDIO_EXTENSIONS.has(extension)) return "audio"
	return "unknown"
}

export const isVideoMediaUrl = (url?: string | null) => inferMediaKind(url) === "video"

export const registerGameMediaUrlKind = (
	url: string,
	kind: GameMediaKind
) => {
	if (url.startsWith("blob:") && kind !== "unknown") {
		blobUrlKinds.set(url, kind)
	}
}

export const createGameMediaItem = (
	url?: string | null,
	mimeType?: string | null
): GameMediaItem | null => {
	if (!url) return null
	const kind = inferMediaKind(url, mimeType)
	if (kind === "unknown") return null
	return { url, kind }
}
